"use client";
/**
 * "Your offers" inbox (Block 4, overview 7.5).
 *
 * Route: /offers. Entry points: a %-badge on Home + a Profile row.
 *
 * Tabs (Active / Upcoming / Expired) over the categorized offer cards returned
 * by `GET /v1/promotions/customers/{id}/offers`. Each card shows a
 * mechanic-type icon, headline, description, optional copyable code, expiry,
 * and a primary CTA that varies by offer kind:
 *   - code-based  → "Use now" deep-links to the menu with the code pre-applied
 *   - category    → "Browse" (back to the menu)
 *   - autoApplied → passive "Auto-applied" label
 *
 * Fail-soft: the api returns an empty inbox on 404, so this renders the empty
 * state rather than an error when the backend isn't deployed yet.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { Icons } from "@/components/Icons";
import { Button, Card, TopBar } from "@/components/ui";
import { SignedOutGate } from "@/components/loyalty/LoyaltyBits";
import {
  CodeChip,
  OfferIcon,
  formatExpiry,
  formatExpiredAgo,
  isExpiringSoon,
  resolveOfferCta,
  offerUseNowPath,
} from "@/components/offers/OfferBits";
import { useOffersInbox } from "@/hooks/useOffers";
import { useAuth } from "@/store/auth";
import { track } from "@/lib/analytics";
import type { OfferCard } from "@/api/services";

type TabKey = "active" | "upcoming" | "expired";

function OfferCardView({
  offer,
  tab,
  onUseNow,
  onBrowse,
  t,
  lang,
}: {
  offer: OfferCard;
  tab: TabKey;
  onUseNow: (offer: OfferCard) => void;
  onBrowse: () => void;
  t: TFunction;
  lang: string;
}) {
  const expired = tab === "expired";
  const redeemed = offer.badge === "redeemed";
  const cta = resolveOfferCta(offer);
  const expiry = expired
    ? formatExpiredAgo(offer.expiresAt, t)
    : formatExpiry(offer.expiresAt, t, lang);
  const showNewBadge = offer.badge === "new" && !expired;
  const showExpiringBadge =
    !expired && (offer.badge === "expiring" || isExpiringSoon(offer.expiresAt));

  return (
    <Card
      padding={14}
      style={{
        position: "relative",
        opacity: expired ? 0.62 : 1,
        filter: expired ? "grayscale(0.6)" : "none",
        border: showExpiringBadge ? "1px solid var(--primary-200)" : "1px solid transparent",
      }}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <OfferIcon rewardKind={offer.rewardKind} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span className="display" style={{ fontSize: 15 }}>
              {offer.headline}
            </span>
            {showNewBadge && <span className="badge primary">{t("v2.offers.new")}</span>}
            {showExpiringBadge && (
              <span
                className="badge"
                style={{ background: "#fff0e7", color: "#e8552d" }}
              >
                {t("v2.offers.expiring")}
              </span>
            )}
            {redeemed && (
              <span
                className="badge"
                style={{ background: "var(--surface-2)", color: "var(--ink-2)" }}
              >
                {t("v2.offers.redeemed")}
              </span>
            )}
          </div>

          {offer.description && (
            <div className="muted" style={{ fontSize: 12.5, marginTop: 3, lineHeight: 1.45 }}>
              {offer.description}
            </div>
          )}

          {/* Code + expiry meta row */}
          {(offer.code || expiry) && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
                marginTop: 10,
              }}
            >
              {offer.code && !expired && <CodeChip code={offer.code} t={t} />}
              {expiry && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: 12,
                    color: "var(--ink-3)",
                  }}
                >
                  <Icons.clock size={12} />
                  {expiry}
                </span>
              )}
            </div>
          )}

          {/* Primary CTA — hidden on the expired tab. */}
          {!expired && (
            <div style={{ marginTop: 12 }}>
              {cta === "auto_applied" ? (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: "var(--primary-700)",
                  }}
                >
                  <Icons.checkCircle size={14} />
                  {t("v2.offers.auto_applied")}
                </span>
              ) : cta === "use_now" ? (
                <Button size="sm" onClick={() => onUseNow(offer)}>
                  {t("v2.offers.use_now")}
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={onBrowse}>
                  {t("v2.offers.browse")}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function TabBar({
  tab,
  setTab,
  counts,
  t,
}: {
  tab: TabKey;
  setTab: (k: TabKey) => void;
  counts: Record<TabKey, number>;
  t: TFunction;
}) {
  const tabs: Array<{ key: TabKey; label: string }> = [
    { key: "active", label: t("v2.offers.tab_active") },
    { key: "upcoming", label: t("v2.offers.tab_upcoming") },
    { key: "expired", label: t("v2.offers.tab_expired") },
  ];
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: 4,
        background: "var(--surface-2)",
        borderRadius: "var(--radius-chip)",
        padding: 4,
        border: "1px solid var(--border)",
      }}
    >
      {tabs.map((tb) => {
        const on = tab === tb.key;
        const count = counts[tb.key];
        return (
          <button
            key={tb.key}
            onClick={() => setTab(tb.key)}
            style={{
              height: 36,
              borderRadius: "var(--radius-chip)",
              border: 0,
              fontWeight: 700,
              fontSize: 13,
              background: on ? "var(--surface-1)" : "transparent",
              color: on ? "var(--ink-1)" : "var(--ink-2)",
              boxShadow: on ? "0 1px 2px rgba(0,0,0,0.07)" : "none",
              cursor: "pointer",
              fontFamily: "inherit",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 5,
            }}
          >
            {tb.label}
            {tb.key !== "expired" && count > 0 && (
              <span
                className="num"
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: on ? "var(--primary-700)" : "var(--ink-3)",
                }}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function OffersInboxScreen() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const { isLoggedIn } = useAuth();
  const { loading, error, inbox, refresh } = useOffersInbox();
  const [tab, setTab] = useState<TabKey>("active");

  if (!isLoggedIn) {
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.offers.title")} onBack={() => navigate("/profile")} />
        <SignedOutGate
          icon={<Icons.ticket size={32} />}
          title={t("v2.offers.signin_title")}
          desc={t("v2.offers.signin_desc")}
          cta={t("v2.profile.signin_phone")}
        />
      </div>
    );
  }

  const onUseNow = (offer: OfferCard) => {
    if (!offer.code) return;
    track("offer_use_now", { promotion_id: offer.promotionId, surface: "inbox" });
    navigate(offerUseNowPath(offer.code));
  };
  const onBrowse = () => navigate("/");

  const counts: Record<TabKey, number> = {
    active: inbox.active.length,
    upcoming: inbox.upcoming.length,
    expired: inbox.expired.length,
  };
  const list =
    tab === "active" ? inbox.active : tab === "upcoming" ? inbox.upcoming : inbox.expired;

  const emptyCopy =
    tab === "active"
      ? t("v2.offers.empty_active")
      : tab === "upcoming"
        ? t("v2.offers.empty_upcoming")
        : t("v2.offers.empty_expired");

  return (
    <div style={{ paddingBottom: 80 }}>
      <TopBar
        title={t("v2.offers.title")}
        onBack={() => navigate("/profile")}
        right={
          <Button variant="ghost" size="sm" onClick={() => refresh()} disabled={loading}>
            {t("v2.common.retry")}
          </Button>
        }
      />

      <div style={{ padding: "12px 16px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
        <TabBar tab={tab} setTab={setTab} counts={counts} t={t} />

        {error && (
          <Card padding={14} style={{ borderColor: "#fecaca", background: "#fff7f7" }}>
            <div style={{ fontSize: 13, color: "#991b1b", fontWeight: 700 }}>
              {t("v2.loyalty.couldnt_load")}
            </div>
          </Card>
        )}

        {loading && list.length === 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[0, 1].map((i) => (
              <Card key={i} padding={14} style={{ boxShadow: "none" }}>
                <div style={{ display: "flex", gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: "var(--surface-2)" }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ width: "60%", height: 14, borderRadius: 6, background: "var(--surface-2)" }} />
                    <div style={{ width: "85%", height: 11, borderRadius: 6, background: "var(--surface-2)", marginTop: 8 }} />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : list.length === 0 ? (
          <div style={{ padding: "40px 24px", textAlign: "center", color: "var(--ink-2)" }}>
            <div
              style={{
                width: 76,
                height: 76,
                borderRadius: 999,
                background: "var(--surface-2)",
                display: "grid",
                placeItems: "center",
                color: "var(--ink-3)",
                margin: "0 auto 14px",
              }}
            >
              <Icons.ticket size={32} />
            </div>
            <div style={{ fontSize: 13.5, lineHeight: 1.5, maxWidth: 280, margin: "0 auto" }}>
              {emptyCopy}
            </div>
            {tab !== "expired" && (
              <Button variant="soft" onClick={onBrowse} style={{ marginTop: 16 }}>
                {t("v2.offers.browse_popular")}
              </Button>
            )}
          </div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {list.map((offer, i) => (
              <OfferCardView
                key={offer.promotionId ?? i}
                offer={offer}
                tab={tab}
                onUseNow={onUseNow}
                onBrowse={onBrowse}
                t={t}
                lang={lang}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
